/**
 * What reading the sampled images produced, and the ways the operator can act on it.
 *
 * A reading is offered rather than taken: only the operator knows whether a sampled asset is
 * representative of the style or a stray, so every notice here reports and waits.
 *
 * Every tally below travels to i18next as `matches`, never `count`, which it reads as a plural selector.
 */
import { ReactNode } from "react";
import { t } from "../../../i18n";
import { AssetDimensions } from "./assetSample";
import { StyleSize } from "./centralImagesDocument";
import { AssetMeasurementOutcome, AssetSampleMeasurement } from "./measureAssetSize";

interface Props {
  /** The last measurement's outcome, or `undefined` while none has run. */
  measurement: AssetMeasurementOutcome | undefined;
  /** The size the fields hold, which an agreeing reading is measured against. */
  draftSize: StyleSize | undefined;
  /** Whether the sample lookup failed, which leaves nothing to measure. */
  samplesFailed: boolean;
  /** Whether the lookup succeeded but turned up no game carrying an image in the chosen style. */
  styleHasNoSamples: boolean;
  /** Whether a reading is on its way but has nothing to work from yet. */
  readingPending: boolean;
  locked: boolean;
  onAdopt: (dimensions: AssetDimensions) => void;
}

interface NoticeProps {
  /** The Bootstrap alert variant, which is what the operator reads a notice's standing off. */
  variant: "info" | "success" | "warning";
  testId: string;
  children: ReactNode;
}

/** The shell every notice here is drawn in, so none of them can be left out of being announced. */
function Notice({ variant, testId, children }: NoticeProps): JSX.Element {
  return (
    <div
      className={`alert alert-${variant}`}
      role="alert"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

interface AdoptButtonProps {
  dimensions: AssetDimensions;
  locked: boolean;
  onAdopt: (dimensions: AssetDimensions) => void;
}

/**
 * The offer to record a reading the fields do not already hold. The states that raise it are
 * mutually exclusive — an agreement the fields hold no size for, and a conflict with the size they
 * hold — so the one test id is what the operator's single way out of both is found by.
 */
function AdoptButton({ dimensions, locked, onAdopt }: AdoptButtonProps): JSX.Element {
  return (
    <button
      className="btn btn-sm btn-outline-dark"
      type="button"
      data-testid="enforced-style-use-measured"
      disabled={locked}
      onClick={() => onAdopt(dimensions)}
    >
      {t("enforced-thumbnail-style.use-size", { width: dimensions.width, height: dimensions.height }, `Use ${dimensions.width}×${dimensions.height}`)}
    </button>
  );
}

/** How many of the samples came back with dimensions, which is what an agreement rests on. */
function countMeasured(samples: AssetSampleMeasurement[]): number {
  return samples.filter((sample) => sample.dimensions !== undefined).length;
}

function sameDimensions(left: AssetDimensions, right: AssetDimensions): boolean {
  return left.width === right.width && left.height === right.height;
}

/** Every distinct reading the samples produced, in the order first seen, for the operator to choose between. */
function distinctReadings(samples: AssetSampleMeasurement[]): AssetDimensions[] {
  return samples.reduce<AssetDimensions[]>((readings, sample) => {
    const dimensions = sample.dimensions;
    if (dimensions !== undefined && !readings.some((seen) => sameDimensions(seen, dimensions))) {
      readings.push(dimensions);
    }
    return readings;
  }, []);
}

function countReadings(samples: AssetSampleMeasurement[], dimensions: AssetDimensions): number {
  return samples.filter((sample) => sample.dimensions !== undefined && sameDimensions(sample.dimensions, dimensions)).length;
}

export function MeasurementNotices({ measurement, draftSize, samplesFailed, styleHasNoSamples, readingPending, locked, onAdopt }: Props): JSX.Element {
  const agreedConflict =
    measurement?.status === "agreed" && draftSize !== undefined && !sameDimensions(measurement.dimensions, draftSize) ? measurement.dimensions : undefined;

  // An agreement the fields hold no size to be measured against. A reading is offered rather than
  // taken, so one reported beside fields it did not fill has to be as takeable as a conflicting one
  // is — otherwise retyping the size by hand is the only way left to record what was just read.
  const agreedUnfilled = measurement?.status === "agreed" && draftSize === undefined ? measurement.dimensions : undefined;

  return (
    <>
      {readingPending && (
        <Notice
          variant="info"
          testId="enforced-style-reading-pending"
        >
          {t(
            "enforced-thumbnail-style.reading-pending",
            undefined,
            "Looking for games to read this style's image sizes from. A size already in the fields is left as it stands.",
          )}
        </Notice>
      )}

      {styleHasNoSamples && !samplesFailed && (
        <Notice
          variant="info"
          testId="enforced-style-no-samples-for-style"
        >
          {t(
            "enforced-thumbnail-style.no-samples-for-style",
            undefined,
            "None of the games looked at carry an image in this style, so there is nothing to measure or preview. Enter the width and height manually.",
          )}
        </Notice>
      )}

      {samplesFailed && (
        <Notice
          variant="info"
          testId="enforced-style-samples-unavailable"
        >
          {t(
            "enforced-thumbnail-style.samples-unavailable",
            undefined,
            "Sample games could not be loaded, so image sizes cannot be read here. Enter the width and height manually.",
          )}
        </Notice>
      )}

      {measurement?.status === "blocked" && (
        <Notice
          variant="info"
          testId="enforced-style-measurement-blocked"
        >
          {t(
            "enforced-thumbnail-style.measurement-blocked",
            undefined,
            "The image dimensions could not be read from the sampled games. Enter the width and height manually.",
          )}
        </Notice>
      )}

      {measurement?.status === "agreed" && agreedConflict === undefined && (
        <Notice
          // A green notice reads as a state reached, which an agreement no field holds is not.
          variant={agreedUnfilled === undefined ? "success" : "info"}
          testId="enforced-style-measurement-agreed"
        >
          <p>
            {/* An agreement is drawn from the samples that could be read, so a reading taken from
                some of them must not be reported as if every image had been seen. */}
            {countMeasured(measurement.samples) === measurement.samples.length
              ? t(
                  "enforced-thumbnail-style.measurement-agreed",
                  { width: measurement.dimensions.width, height: measurement.dimensions.height },
                  `The sampled images all measure ${measurement.dimensions.width}×${measurement.dimensions.height}.`,
                )
              : t(
                  "enforced-thumbnail-style.measurement-agreed-partial",
                  {
                    width: measurement.dimensions.width,
                    height: measurement.dimensions.height,
                    matches: countMeasured(measurement.samples),
                    total: measurement.samples.length,
                  },
                  `${countMeasured(measurement.samples)} of ${measurement.samples.length} sampled images could be read, and those all measure ${measurement.dimensions.width}×${measurement.dimensions.height}.`,
                )}
            {/* The fields reach this branch blank, half filled, or holding a value the platform
                would not store, and the wording has to hold for all three. */}
            {agreedUnfilled !== undefined &&
              ` ${t(
                "enforced-thumbnail-style.measurement-agreed-unfilled",
                undefined,
                "The fields do not hold a usable size yet — take the measured size below if that is the one to record.",
              )}`}
          </p>
          {agreedUnfilled !== undefined && (
            <AdoptButton
              dimensions={agreedUnfilled}
              locked={locked}
              onAdopt={onAdopt}
            />
          )}
        </Notice>
      )}

      {agreedConflict !== undefined && draftSize !== undefined && (
        <Notice
          variant="warning"
          testId="enforced-style-size-conflict"
        >
          <p>
            {t(
              "enforced-thumbnail-style.measurement-conflict",
              { measuredWidth: agreedConflict.width, measuredHeight: agreedConflict.height, width: draftSize.width, height: draftSize.height },
              `The sampled images measure ${agreedConflict.width}×${agreedConflict.height}, but the fields hold ${draftSize.width}×${draftSize.height}. A size already in the fields is left as it stands — take the measured size below if that is the one to record.`,
            )}
          </p>
          <AdoptButton
            dimensions={agreedConflict}
            locked={locked}
            onAdopt={onAdopt}
          />
        </Notice>
      )}

      {measurement?.status === "disagreed" && (
        <Notice
          variant="warning"
          testId="enforced-style-measurement-disagreement"
        >
          <p>
            {t(
              "enforced-thumbnail-style.measurement-disagreement",
              undefined,
              "The sampled images do not all have the same dimensions. Pick the size to record, or type one — the images may be a mix, or the url template may point at the wrong style.",
            )}
          </p>
          <ul>
            {distinctReadings(measurement.samples).map((reading) => (
              <li key={`${reading.width}x${reading.height}`}>
                <button
                  className="btn btn-sm btn-outline-dark"
                  type="button"
                  data-testid="enforced-style-reading"
                  disabled={locked}
                  onClick={() => onAdopt(reading)}
                >
                  {t(
                    "enforced-thumbnail-style.use-reading",
                    { width: reading.width, height: reading.height, matches: countReadings(measurement.samples, reading), total: measurement.samples.length },
                    `Use ${reading.width}×${reading.height} (${countReadings(measurement.samples, reading)} of ${measurement.samples.length} images)`,
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Notice>
      )}
    </>
  );
}
